import { useState } from "react";

import {
  makeGetApiCall,
  makePostApiCall,
  makePutApiCall,
} from "../../../shared/makeApiCall";
import SubcategoryDetailsForm from "../../../components/forms/setup/subcategories/SubcategoryDetailsForm";
import { BannerNotificationType } from "../../../components/SimpleNotification";
import DeleteConfirmationForm from "../../../components/forms/DeleteConfirmationForm";
import {
  generateUniqueNumber,
  updateObjectInArray,
} from "../../../shared/utility";
import { SnackbarData, SnackbarType } from "../../../components/Snackbar";

export function SubcategoriesViewModel(logout, addSnackbar) {
  const [loading, setLoading] = useState(false);
  const [subcategories, setSubcategories] = useState({
    data: [],
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [showSimpleNotification, setShowSimpleNotification] = useState({
    show: false,
    message: null,
    title: null,
    type: BannerNotificationType.Success,
  });
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorNotification = (callResult, fallbackTitle) => {
    setShowSimpleNotification({
      show: true,
      title:
        callResult?.data?.message || callResult?.data?.title || fallbackTitle,
      type: BannerNotificationType.Error,
    });
  };

  const getSubcategories = async function (search, page) {
    setLoading(true);

    let callResult = await makePostApiCall(
      `subcategories?page=${page}&search=${search}`,
    );
    setLoading(false);
    if (callResult.success) {
      setSubcategories(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not fetch subcategories");
    }
  };

  const getCategoryOptions = async function () {
    let callResult = await makeGetApiCall("categoriesForDropdown");
    if (callResult.success) {
      setCategoryOptions(callResult.data || []);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not fetch categories");
    }
  };

  const createSubcategory = async function (values) {
    const payload = { ...values };
    let callResult = await makePostApiCall("subcategories/create", payload);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult?.data?.message || "Subcategory added",
          SnackbarType.Success,
        ),
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getSubcategories("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not create subcategory");
    }
  };

  const updateSubcategory = async function (values, index) {
    let callResult = await makePutApiCall(`subcategories/${values.id}`, values);

    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult?.data?.message || "Subcategory updated",
          SnackbarType.Success,
        ),
      );
      setSlideOver({
        childComponent: null,
        open: false,
      });

      const currentItems = [...subcategories.data];
      var payload = {
        index: index,
        item: callResult.data.item,
      };
      const newItems = updateObjectInArray(currentItems, payload);
      setSubcategories({ ...subcategories, data: newItems });
      getSubcategories("", 1);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not update subcategory");
    }
  };

  const openAddSubcategorySlideOver = (isEdit, subcategory, index) => {
    setSlideOver({
      childComponent: (
        <SubcategoryDetailsForm
          subcategory={subcategory}
          categoryOptions={categoryOptions}
          isEdit={isEdit}
          setOpen={setSlideOver}
          actionCall={(payload) =>
            isEdit
              ? updateSubcategory(payload, index)
              : createSubcategory(payload)
          }
          title={isEdit ? `Update ${subcategory.name}` : "Add new subcategory"}
        />
      ),
      open: true,
    });
  };

  const deleteSubcategory = async function (id, index) {
    setLoading(true);

    let callResult = await makePostApiCall(`deleteSubcategory/${id}`);
    setLoading(false);
    if (callResult.success) {
      setEmptyModelOpen({
        open: false,
        childComponent: null,
      });

      const newItems = subcategories.data.filter((item) => item.id !== id);
      setSubcategories({ ...subcategories, data: newItems });

      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorNotification(callResult, "Could not delete subcategory");
    }
  };

  const askDeleteSubcategory = function (subcategory, index) {
    setEmptyModelOpen({
      childComponent: (
        <DeleteConfirmationForm
          buttonMessage="Delete"
          confirmAction={() => deleteSubcategory(subcategory.id, index)}
          message={`Are you sure you want to delete  ${subcategory.name}?`}
          setOpen={setEmptyModelOpen}
          hideReason={true}
        />
      ),
      open: true,
    });
  };

  return {
    openAddSubcategorySlideOver,
    getSubcategories,
    getCategoryOptions,
    loading,
    subcategories,
    setSlideOver,
    slideOver,
    setShowSimpleNotification,
    showSimpleNotification,
    setEmptyModelOpen,
    emptyModal,
    askDeleteSubcategory,
  };
}
